import { ArrowBack } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { myFolder } from '../../redux/consts';
import { selectConversations } from '../../redux/reducers/conversationReducer';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { Prompt } from '../../Types/prompt';
import { getPromptOwnerName } from '../../util/promptMetadata';
import { FolderPath, NewChatPath, TemporaryChatPath } from '../../util/routesDef';
import AutoTruncatingContainerWithShowMore from '../AutoTruncatingContainerWithShowMore';
import AutoTruncatingTypographyWithTooltip from '../AutoTruncatingTypographyWithTooltip';
import ConfirmationDialog from '../DealGPT/PhaseTwo/ConfirmationDialog';
import { useChat } from '../Providers/ChatProvider';
import { useDrawer } from '../Providers/DrawerProvider';

const PromptCardDetails = ({ prompt, onBack }: { prompt: Prompt; onBack: () => void }) => {
  const { closeDrawer } = useDrawer();
  const navigate = useClassicNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { getChatPrompt } = useChat();
  const { currentFolder } = useSelector(selectFolders);
  const { currentConversation } = useSelector(selectConversations);
  const [open, setOpen] = useState(false);

  const navigateToChat = useCallback(() => {
    // Clone and update query params
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set('prompt', prompt.id);

    // Check if we're in temporary chat mode
    const isTemporaryChat = location.pathname.includes(TemporaryChatPath);

    let path: string;
    if (isTemporaryChat) {
      // For temporary chat, always use temporary chat route
      path = `/${TemporaryChatPath}?${updatedParams.toString()}`;
    } else {
      // Folder segment (currentFolder id or myFolder id)
      const folderPathSegment =
        currentFolder?.id && currentFolder.id !== myFolder.id
          ? `${currentFolder.id}`
          : `${myFolder.id}`;

      // Chat segment (chat id or new-chat)
      const chatPathSegment = currentConversation?.id ? `${currentConversation.id}` : NewChatPath;

      path = `${FolderPath}/${folderPathSegment}/${chatPathSegment}?${updatedParams.toString()}`;
    }

    closeDrawer();
    navigate(path);
  }, [
    prompt,
    navigate,
    closeDrawer,
    currentFolder,
    currentConversation,
    searchParams,
    location.pathname,
  ]);

  const handleUsePrompt = useCallback(() => {
    if (!prompt) return;
    const currentPrompt = getChatPrompt();
    if (currentPrompt) {
      setOpen(true);
    } else {
      navigateToChat();
    }
  }, [prompt, getChatPrompt, navigateToChat]);

  return (
    <Box display="flex" flexDirection="column" px="48px" gap={2}>
      <Box display="flex" alignItems="center" gap={2}>
        <Tooltip title="Return to Library" placement="top">
          <IconButton
            onClick={onBack}
            sx={{ height: '24px', width: '24px', marginRight: '10px', color: '#070154' }}
          >
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <AutoTruncatingTypographyWithTooltip
          text={prompt.title}
          variant="h5"
          clampValue={3}
          sx={{ fontWeight: '700px' }}
        />
      </Box>
      <div>
        <Typography variant="subtitle1">Solution Type</Typography>
        <Typography sx={{ paddingTop: 2 }}>{prompt.solutionType ?? 'Prompt'}</Typography>
      </div>
      <div>
        <Typography variant="subtitle1">Owner</Typography>
        <Typography sx={{ paddingTop: 2 }}>{getPromptOwnerName(prompt)}</Typography>
      </div>
      <div>
        <Typography variant="subtitle1">Description</Typography>
        <AutoTruncatingContainerWithShowMore
          variant="body1"
          clampValue={5}
          text={prompt.description}
          sx={{ paddingTop: 2 }}
        />
      </div>
      <div style={{ marginBottom: '80px' }}>
        <Typography variant="subtitle1">Prompt</Typography>
        <AutoTruncatingContainerWithShowMore
          variant="body1"
          clampValue={5}
          text={prompt.prompt}
          sx={{ paddingTop: 2 }}
        />
      </div>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          position: 'fixed',
          width: 'stretch',
          bottom: 0,
          py: '8px',
          marginLeft: '-48px',
          paddingRight: '48px',
          borderTop: '1px solid #CED7E6',
          backgroundColor: 'white',
        }}
      >
        <Button variant="contained" onClick={handleUsePrompt}>
          Use Prompt
        </Button>
      </Box>

      {open && (
        <ConfirmationDialog
          title="Confirmation Dialog"
          message="Your current text input will be replaced with the selected prompt content from the
            library. This action cannot be undone."
          confirmLabel="I Understand"
          close={() => setOpen(false)}
          confirm={navigateToChat}
        />
      )}
    </Box>
  );
};

export default PromptCardDetails;
